import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Sparkles, Key, ArrowRight, User, Server, Shield, RefreshCw, Check } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

interface FlowStep {
    id: number;
    from: string;
    to: string;
    label: string;
    data?: string;
    completed: boolean;
}

const OAUTH_STEPS: Omit<FlowStep, 'completed'>[] = [
    { id: 1, from: 'user', to: 'app', label: 'Click "Login with Google"', data: undefined },
    { id: 2, from: 'app', to: 'google', label: 'Redirect to Google', data: 'client_id, redirect_uri, scope' },
    { id: 3, from: 'user', to: 'google', label: 'User logs in & consents', data: undefined },
    { id: 4, from: 'google', to: 'app', label: 'Authorization Code', data: 'code=abc123' },
    { id: 5, from: 'app', to: 'google', label: 'Exchange code for token', data: 'code + client_secret' },
    { id: 6, from: 'google', to: 'app', label: 'Access Token', data: 'access_token=xyz789' },
    { id: 7, from: 'app', to: 'api', label: 'API request with token', data: 'Authorization: Bearer xyz789' },
    { id: 8, from: 'api', to: 'app', label: 'User data returned', data: '{ email, name, picture }' },
];

export const OAuthFlow = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const playStep = async () => {
        if (currentStep >= OAUTH_STEPS.length) return;

        setIsPlaying(true);
        setCurrentStep(currentStep + 1);

        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsPlaying(false);

        if (currentStep >= 3 && !completed) {
            setCompleted(true);
            completeModule('oauth-auth', 100);
        }
    };

    const playAll = async () => {
        for (let i = currentStep; i < OAUTH_STEPS.length; i++) {
            setCurrentStep(i + 1);
            await new Promise(resolve => setTimeout(resolve, 1200));
        }
        if (!completed) {
            setCompleted(true);
            completeModule('oauth-auth', 100);
        }
    };

    const reset = () => {
        setCurrentStep(0);
    };

    const getNodePosition = (node: string) => {
        switch (node) {
            case 'user': return { x: 50, icon: User, color: 'bg-green-500', label: 'You' };
            case 'app': return { x: 200, icon: Shield, color: 'bg-blue-500', label: 'Your App' };
            case 'google': return { x: 350, icon: Key, color: 'bg-red-500', label: 'Google' };
            case 'api': return { x: 500, icon: Server, color: 'bg-purple-500', label: 'Google API' };
            default: return { x: 0, icon: User, color: 'bg-slate-500', label: node };
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-30">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-orange-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="hidden md:flex bg-green-900 text-green-400 px-3 py-1 rounded-full text-xs font-bold items-center gap-1"><Sparkles size={12} /> Module Complete</div>}
                    <div className="bg-orange-900/50 text-orange-400 px-4 py-1 rounded-full text-sm font-bold border border-orange-800">
                        🔑 Token Exchange
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-white mb-2 flex items-center justify-center gap-3">
                        <Key className="text-orange-500" /> OAuth 2.0 Flow
                    </h1>
                    <p className="text-slate-400 max-w-2xl mx-auto">
                        OAuth lets users log into your app using Google/GitHub without sharing their password.
                        Watch the authorization code flow step by step!
                    </p>
                </div>

                {/* Controls */}
                <div className="flex justify-center gap-4 mb-8">
                    <button
                        onClick={playStep}
                        disabled={isPlaying || currentStep >= OAUTH_STEPS.length}
                        className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${isPlaying || currentStep >= OAUTH_STEPS.length
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                : 'bg-orange-600 hover:bg-orange-500 text-white'
                            }`}
                    >
                        <ArrowRight size={18} /> Next Step
                    </button>
                    <button
                        onClick={playAll}
                        disabled={isPlaying || currentStep >= OAUTH_STEPS.length}
                        className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl font-bold transition-colors"
                    >
                        Play All
                    </button>
                    <button onClick={reset} className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors">
                        <RefreshCw size={18} />
                    </button>
                </div>

                {/* Progress */}
                <div className="flex justify-center gap-2 mb-8">
                    {OAUTH_STEPS.map((_, i) => (
                        <motion.div
                            key={i}
                            className={`w-3 h-3 rounded-full ${i < currentStep ? 'bg-orange-500' : 'bg-slate-700'}`}
                            animate={{ scale: i === currentStep - 1 ? [1, 1.3, 1] : 1 }}
                        />
                    ))}
                </div>

                {/* Visualization */}
                <div className="bg-slate-800 rounded-3xl p-8 border border-slate-700 mb-8">
                    {/* Nodes */}
                    <div className="flex justify-between items-start mb-8">
                        {['user', 'app', 'google', 'api'].map(node => {
                            const pos = getNodePosition(node);
                            const Icon = pos.icon;
                            const isActive = OAUTH_STEPS[currentStep - 1]?.from === node || OAUTH_STEPS[currentStep - 1]?.to === node;

                            return (
                                <motion.div
                                    key={node}
                                    animate={{ scale: isActive ? 1.1 : 1 }}
                                    className="flex flex-col items-center"
                                >
                                    <motion.div
                                        className={`w-20 h-20 ${pos.color} rounded-2xl flex items-center justify-center text-white shadow-lg`}
                                        animate={{
                                            boxShadow: isActive ? `0 0 30px ${pos.color === 'bg-green-500' ? 'rgba(34,197,94,0.5)' : pos.color === 'bg-blue-500' ? 'rgba(59,130,246,0.5)' : pos.color === 'bg-red-500' ? 'rgba(239,68,68,0.5)' : 'rgba(168,85,247,0.5)'}` : 'none'
                                        }}
                                    >
                                        <Icon size={32} />
                                    </motion.div>
                                    <span className="mt-2 font-bold text-sm text-slate-400">{pos.label}</span>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Current Step Details */}
                    <AnimatePresence mode="wait">
                        {currentStep > 0 && currentStep <= OAUTH_STEPS.length && (
                            <motion.div
                                key={currentStep}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="bg-slate-900 rounded-2xl p-6"
                            >
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold">
                                        {currentStep}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 text-lg font-bold text-white">
                                            <span className="text-slate-400">{getNodePosition(OAUTH_STEPS[currentStep - 1].from).label}</span>
                                            <ArrowRight className="text-orange-500" size={20} />
                                            <span className="text-slate-400">{getNodePosition(OAUTH_STEPS[currentStep - 1].to).label}</span>
                                        </div>
                                        <p className="text-orange-400">{OAUTH_STEPS[currentStep - 1].label}</p>
                                    </div>
                                </div>

                                {OAUTH_STEPS[currentStep - 1].data && (
                                    <div className="bg-slate-800 rounded-xl p-4 font-mono text-sm text-green-400 border border-slate-700">
                                        {OAUTH_STEPS[currentStep - 1].data}
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {currentStep === 0 && (
                        <div className="text-center text-slate-600 py-8">
                            <Key size={48} className="mx-auto mb-4 opacity-50" />
                            <p>Click "Next Step" to begin the OAuth flow</p>
                        </div>
                    )}

                    {currentStep >= OAUTH_STEPS.length && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-8"
                        >
                            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check size={32} className="text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-green-400">OAuth Complete!</h3>
                            <p className="text-slate-400">User is now authenticated</p>
                        </motion.div>
                    )}
                </div>

                {/* Step List */}
                <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                    <h3 className="font-bold text-white mb-4">All Steps</h3>
                    <div className="space-y-2">
                        {OAUTH_STEPS.map((step, i) => (
                            <div
                                key={step.id}
                                className={`flex items-center gap-4 p-3 rounded-xl transition-colors ${i < currentStep ? 'bg-green-900/20' : 'bg-slate-900/50'
                                    }`}
                            >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < currentStep ? 'bg-green-500 text-white' : 'bg-slate-700 text-slate-400'
                                    }`}>
                                    {i < currentStep ? <Check size={14} /> : step.id}
                                </div>
                                <span className={`text-sm ${i < currentStep ? 'text-green-400' : 'text-slate-500'}`}>
                                    {step.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
